import {
  Users,
  ShoppingBag,
  Shirt,
  Briefcase,
  Package,
  Store,
  Gift,
  Leaf,
  Shield,
  Settings,
  Recycle,
  Clock,
} from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { WhyChooseUsSection } from './WhyChooseUsSection';

const INDUSTRY_ITEMS = [
  { label: 'Retail & Supermarkets', icon: Store },
  { label: 'Fashion & Apparel', icon: Shirt },
  { label: 'Corporate & Promotional Agencies', icon: Briefcase },
  { label: 'FMCG Brands', icon: Package },
  { label: 'E-commerce & D2C Brands', icon: ShoppingBag },
  { label: 'Hospitality & Events', icon: Users },
  { label: 'Gift & Lifestyle Brands', icon: Gift },
];

const WHAT_WE_STAND_FOR_ITEMS = [
  { text: 'Ethical sourcing and responsible production', icon: Leaf },
  { text: 'Quality that meets global retail and export standards', icon: Shield },
  { text: 'Flexible MOQs and customization for every scale', icon: Settings },
  { text: 'Long-lasting, reusable products that reduce single-use plastic', icon: Recycle },
  { text: 'Transparent processes and reliable delivery timelines', icon: Clock },
];

export const AboutPage = ({ onRequestQuote }) => {
  const baseUrl = process.env.PUBLIC_URL || '';

  return (
    <div className="bg-white min-h-screen">
      {/* 1. Hero — Left text + Right banner image */}
      <section className="pt-24 md:pt-28 pb-[60px] md:pb-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-[30px] items-center">
            <div className="max-w-xl">
              <p className=" text-xs tracking-[0.25em] uppercase text-[#2F4F3E] mb-3">
                Our Story
              </p>
              <h1 className=" text-3xl sm:text-4xl lg:text-5xl text-[#2B2B2B] mb-6 leading-tight">
                About Bags4Ever
              </h1>
              <p className=" text-[#5A5A5A] text-base leading-relaxed mb-4">
                Bags4Ever Inc. is a U.S.-based supplier of custom cotton canvas, jute, and
                promotional bags, serving businesses, retailers, wineries, coffee brands, event
                organizers, and corporate customers across the United States.
              </p>
              <p className=" text-[#5A5A5A] text-base leading-relaxed mb-4">
                Our strength comes from combining U.S.-based customer support with dedicated
                manufacturing and sourcing operations in India. This allows us to offer competitive
                factory-direct pricing, reliable quality control, flexible customization, and
                door-delivered supply solutions.
              </p>
              <p className=" text-[#5A5A5A] text-base leading-relaxed mb-4">
                We specialize in custom-made bags designed to match our customers&apos; branding
                and packaging requirements. Whether you need cotton canvas tote bags, jute shopping
                bags, wine bags, promotional bags, or retail packaging solutions, our team works
                closely with you from sampling to final delivery.
              </p>
              <p className=" text-[#5A5A5A] text-base leading-relaxed mb-4">
                Sustainability is at the core of what we do. We focus on reusable, eco-friendly bag
                solutions that help businesses reduce dependence on single-use packaging while
                maintaining quality, functionality, and brand appeal.
              </p>
              <p className=" text-[#5A5A5A] text-base leading-relaxed mb-6">
                At Bags4Ever, our goal is simple: to provide dependable manufacturing, consistent
                quality, and responsive service that helps our customers build stronger brands.
              </p>
              <p className="text-[#2F4F3E] text-base sm:text-lg font-bold leading-snug tracking-wide">
                Factory-Direct Manufacturing. Custom Solutions. Reliable Delivery.
              </p>
            </div>
            <div className="relative aspect-[4/3] lg:aspect-auto lg:min-h-[400px] rounded-lg overflow-hidden bg-[#F5F2ED]">
              <img
                src={`${baseUrl}/6.png`}
                alt="Bags4Ever eco bag collection"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 2. About — Two-column text + image, balanced spacing */}
      <section className="py-[60px] md:py-20 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-[30px] items-center">
            <div className="max-w-xl">
              <h2 className=" text-2xl sm:text-3xl text-[#2B2B2B] mb-6">
                Our Mission
              </h2>
              <p className=" text-[#5A5A5A] text-base leading-relaxed mb-6">
                To deliver custom cotton canvas, jute, and promotional bags that combine
                sustainability, quality, and value, helping businesses create lasting impressions
                through responsible packaging.
              </p>
              <p className=" text-[#5A5A5A] text-base leading-relaxed mb-6">
                Bags4Ever combines:
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  'Eco-friendly materials',
                  'Custom design and manufacturing',
                  'Quality-focused production',
                  'Reliable supply and delivery',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-[#2B2B2B]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2F4F3E] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className=" text-[#5A5A5A] text-base leading-relaxed">
                We believe strong partnerships are built through consistent quality, transparent
                communication, and dependable service.
              </p>
            </div>
            <div className="relative aspect-[4/3] min-h-[280px] rounded-lg overflow-hidden bg-[#F5F2ED]">
              <img
                src={`${baseUrl}/18.png`}
                alt="Premium sustainable bag"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 3. What We Stand For — Centered heading + icon cards (no product images) */}
      <section className="py-[60px] md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className=" text-2xl sm:text-3xl text-[#2B2B2B] mb-10 md:mb-12 text-center">
            What We Stand For
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-[30px] max-w-5xl mx-auto">
            {WHAT_WE_STAND_FOR_ITEMS.map((item, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center p-6 rounded-lg border border-[#E8E8E8] bg-white shadow-sm min-h-[180px] justify-start"
              >
                <item.icon className="w-10 h-10 text-[#2F4F3E] mb-4 flex-shrink-0" />
                <p className=" text-[#5A5A5A] text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Product Categories — 3-column card grid with image on top */}
      <section className="py-[60px] md:py-20 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12 md:mb-14">
            <h2 className=" text-2xl sm:text-3xl text-[#2B2B2B] mb-6">
              What We Offer
            </h2>
            <p className=" text-[#5A5A5A] text-base leading-relaxed mb-4">
              From jute shoppers, cotton totes, canvas bags, grocery bags, wine bags, promotional
              bags, and custom-printed gift packaging, we manufacture bags designed for:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[#5A5A5A] text-sm">
              {[
                'Retail shopping',
                'Corporate gifting',
                'Trade shows & exhibitions',
                'Promotional campaigns',
                'Luxury packaging',
                'Festive & seasonal occasions',
                'Wedding & event gifting',
                'Sustainable brand packaging solutions',
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#2F4F3E]" />
                  {item}
                </li>
              ))}
            </ul>
            <p className=" text-[#5A5A5A] text-base leading-relaxed mt-6">
              Our bags serve different purposes and occasions, offering versatile and reusable
              alternatives to single-use plastic packaging.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[30px]">
            {[
              { img: '45.png', title: 'Jute & Natural', desc: 'Eco-friendly jute shoppers and natural fiber bags for retail and branding.' },
              { img: '42.png', title: 'Canvas & Cotton', desc: 'Durable canvas and cotton totes for everyday and promotional use.' },
              { img: '34.png', title: 'Custom & Promotional', desc: 'Custom-printed and promotional bags for events and gifting.' },
            ].map((card, i) => (
              <article
                key={i}
                className="bg-white rounded-lg overflow-hidden border border-[#E8E8E8] shadow-sm"
              >
                <div className="flex items-center justify-center aspect-[4/3] min-h-[200px] rounded-xl bg-[#FAFAFA] p-4 overflow-hidden">
                  <img
                    src={`${baseUrl}/${card.img}`}
                    alt={card.title}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="p-6">
                  <h3 className=" text-lg text-[#2B2B2B] mb-2">{card.title}</h3>
                  <p className=" text-sm text-[#5A5A5A] leading-relaxed">{card.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Industries — Text left, image right + icon grid below */}
      <section className="py-[60px] md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-[30px] items-center mb-10 md:mb-12">
            <div className="max-w-xl">
              <h2 className=" text-2xl sm:text-3xl text-[#2B2B2B] mb-6">
                Industries We Serve
              </h2>
              <p className=" text-[#5A5A5A] text-base leading-relaxed">
                With over two decades of industry experience, we cater to a diverse range of
                industries including:
              </p>
            </div>
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-[#F5F2ED] min-h-[240px]">
              <img
                src={`${baseUrl}/3.png`}
                alt="Industries we serve"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-[30px]">
            {INDUSTRY_ITEMS.map((item, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center p-4 rounded-lg border border-[#E8E8E8] bg-[#FAFAFA] hover:border-[#2F4F3E]/30 transition-colors"
              >
                <item.icon className="w-8 h-8 text-[#2F4F3E] mb-2" />
                <span className=" text-sm text-[#2B2B2B]">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <WhyChooseUsSection />

      {/* 7. CTA — Full-width background image + centered text + 2 buttons */}
      <section className="relative py-20 md:py-28 min-h-[380px] flex items-center justify-center bg-[#2F4F3E]">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className=" text-2xl sm:text-3xl lg:text-4xl text-white mb-4">
            Ready to Partner With Us?
          </h2>
          <p className=" text-white/90 text-base sm:text-lg mb-8">
            Export-quality sustainable bags for your brand. Request a quote or explore our
            collection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onRequestQuote}
              className="bg-[#2F4F3E] hover:bg-[#233B2F] text-white rounded-md px-8 py-4 text-sm font-medium"
            >
              Request Quote
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#2B2B2B] rounded-md px-8 py-4 text-sm font-medium bg-transparent"
            >
              <Link to="/collection">View Collection</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
